import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, Navigate} from 'react-router-dom';
import {Link} from "react-router-dom";
import styles from './Registration.module.css';
import AuthService from '../../../services/auth/AuthService';
import {setUser} from '../../../store/userSlice';
import type {RootState} from '../../../store/store';

export default function Registration() {
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        website: '', // honeypot
    });

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);

    // Redirect if already logged in
    if (isLoggedIn) return <Navigate to="/courses" replace/>;

    const handleFormChange = (field, value) => {
        setForm((prev) => ({...prev, [field]: value}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const {firstName, lastName, email, password, confirmPassword} = form;

        if (!firstName || !lastName || !email || !password || !confirmPassword) {
            setError('Please fill in all fields.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        try {
            setLoading(true);
            const user = await AuthService.register(firstName, lastName, email, password, form.website);
            dispatch(setUser(user));
            navigate('/courses');
        } catch (err) {
            setError('Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.card}>

                <h1 className={styles.heading}>Create your account</h1>
                <p className={styles.subheading}>Start learning for free today</p>

                {error && <div className={styles.errorBanner}>{error}</div>}

                <form className={styles.form} onSubmit={handleSubmit} noValidate>

                    {/* Honeypot: hidden from real users; bots fill it and get rejected server-side. */}
                    <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        value={form.website}
                        onChange={(e) => handleFormChange('website', e.target.value)}
                        style={{position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0}}
                    />

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="firstName">First name</label>
                            <input
                                id="firstName"
                                type="text"
                                className={styles.input}
                                placeholder="Jane"
                                value={form.firstName}
                                onChange={(e) => handleFormChange('firstName', e.target.value)}
                                autoComplete="given-name"
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor="lastName">Last name</label>
                            <input
                                id="lastName"
                                type="text"
                                className={styles.input}
                                placeholder="Doe"
                                value={form.lastName}
                                onChange={(e) => handleFormChange('lastName', e.target.value)}
                                autoComplete="family-name"
                            />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="email">Email</label>
                        <input
                            id="email"
                            type="email"
                            className={styles.input}
                            placeholder="you@example.com"
                            value={form.email}
                            onChange={(e) => handleFormChange('email', e.target.value)}
                            autoComplete="email"
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="password">Password</label>
                        <input
                            id="password"
                            type="password"
                            className={styles.input}
                            placeholder="Min. 8 characters"
                            value={form.password}
                            onChange={(e) => handleFormChange('password', e.target.value)}
                            autoComplete="new-password"
                        />
                    </div>

                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="confirmPassword">Confirm password</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            className={styles.input}
                            placeholder="••••••••"
                            value={form.confirmPassword}
                            onChange={(e) => handleFormChange('confirmPassword', e.target.value)}
                            autoComplete="new-password"
                        />
                    </div>

                    <p className={styles.terms}>
                        By signing up you agree to our{' '}
                        <Link href="/terms" className={styles.link}>Terms of Service</Link>{' '}
                        and{' '}
                        <Link href="/privacy" className={styles.link}>Privacy Policy</Link>.
                    </p>

                    <button type="submit" className={styles.button} disabled={loading}>
                        {loading ? <span className={styles.spinner}/> : 'Create account'}
                    </button>

                </form>

                <p className={styles.footer}>
                    Already have an account?{' '}
                    <Link href="/login" className={styles.link}>Sign in</Link>
                </p>

            </div>
        </div>
    );
}
